// SPDX-License-Identifier: MIT
// UserFeedbackDispatcher - centralizes user-facing messages (Phase 5)

import { ClassifiedError } from '../download/types';

export type UserMessageKind = 'info' | 'success' | 'warning' | 'error';

export interface UserMessage {
  id: string;
  kind: UserMessageKind;
  text: string;
  ts: number;
  autoDismissMs?: number;
}

export type MessageListener = (messages: UserMessage[]) => void;

export class UserFeedbackDispatcher {
  private messages: UserMessage[] = [];
  private listeners: MessageListener[] = [];

  push(kind: UserMessageKind, text: string, autoDismissMs?: number) {
    const msg: UserMessage = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      kind,
      text,
      ts: Date.now(),
      autoDismissMs,
    };
    this.messages.push(msg);
    this.emit();
    if (autoDismissMs) {
      setTimeout(() => this.dismiss(msg.id), autoDismissMs);
    }
  }

  errorFromClassification(err: ClassifiedError) {
    const actionable = this.humanize(err);
    this.push('error', actionable, 10000);
  }

  dismiss(id: string) {
    this.messages = this.messages.filter(m => m.id !== id);
    this.emit();
  }

  subscribe(fn: MessageListener) {
    this.listeners.push(fn);
    fn([...this.messages]);
    return () => {
      this.listeners = this.listeners.filter(l => l !== fn);
    };
  }

  private emit() {
    this.listeners.forEach(l => l([...this.messages]));
  }

  private humanize(err: ClassifiedError): string {
    switch (err.category) {
      case 'auth':
        return 'Authentication failed. Please verify SAS token and container permissions.';
      case 'permission':
        return 'Insufficient permissions for requested blob.';
      case 'not-found':
        return 'Requested blob was not found.';
      case 'network':
        return 'Network issue encountered. Will retry automatically if possible.';
      case 'throttle':
        return 'Service is throttling requests. Retrying with backoff.';
      case 'timeout':
        return 'Operation timed out. Consider retrying.';
      case 'integrity':
        return 'File failed integrity checks and was skipped.';
      case 'cancelled':
        return 'Operation cancelled.';
      default:
        return err.message || 'Unexpected error occurred.';
    }
  }
}
